/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Accordion, AccordionHeader, AccordionPanel, SelectField, SelectOption, useTheme } from '@coursera/cds-core';

import DateField from 'bundles/enterprise-admin-user-filters/components/DateField';
import type { DateClause, DateClauseOption } from 'bundles/enterprise-admin-user-filters/types';
import {
  COMMON_DATE_FORMAT,
  getAfterTimestamp,
  getBeforeTimestamp,
  getDateFromTimestamp,
  getDays,
  getTimestampFromDate,
} from 'bundles/enterprise-admin-user-filters/utils';
import { DebouncedTextField } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type Props = {
  options: {
    value: string;
    label: string;
    heading: string | JSX.Element;
  }[];
  dateClause?: DateClause;
  label: string;
  fromValidationLabel?: string;
  fromValidationStatus?: 'error';
  id?: string;
  nDaysAgoField: 'nDaysAgoAfter' | 'nDaysAgoBefore';
  onChange: (dateClause: DateClause) => void;
};

const FilterDateClause: React.VFC<Props> = ({
  options,
  dateClause,
  label,
  fromValidationLabel,
  fromValidationStatus,
  id,
  nDaysAgoField,
  onChange,
}) => {
  const [dateClauseOption, setDateClauseOption] = React.useState<DateClauseOption>(
    // eslint-disable-next-line no-nested-ternary
    !dateClause ? 'ANYTIME' : dateClause.type === 'RELATIVE' ? 'LAST_N_DAYS' : dateClause.before ? 'BETWEEN' : 'AFTER'
  );
  const heading = options.find((_) => _.value === dateClauseOption)?.heading ?? '';
  const theme = useTheme();
  return (
    <Accordion variant="silent" id={id}>
      <AccordionHeader labelProps={{ 'aria-label': _t('Date clause') }} label={heading as React.ReactText} />
      <AccordionPanel>
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: ${theme.spacing(24)};
            padding-top: ${theme.spacing(8)};
          `}
        >
          <SelectField
            css={css`
              ${theme.breakpoints.down('xs')} {
                width: 100%;
                max-width: none;
              }
            `}
            label={label}
            value={dateClauseOption}
            onChange={(event) => {
              const clause = event.target.value as DateClauseOption;
              setDateClauseOption(clause);
              onChange(undefined);
            }}
            data-testid="date-filter-clause"
          >
            {options.map(({ value, label: optionLabel }) => (
              <SelectOption key={value} value={value}>
                {optionLabel}
              </SelectOption>
            ))}
          </SelectField>

          {(dateClauseOption === 'AFTER' || dateClauseOption === 'BETWEEN') && (
            <DateField
              key={dateClauseOption}
              label={
                (
                  <FormattedMessage message={_t('From date, inclusive ({format})')} format={COMMON_DATE_FORMAT} />
                ) as unknown as string
              }
              value={getDateFromTimestamp(getAfterTimestamp(dateClause))}
              onChange={(value) =>
                onChange({
                  type: 'ABSOLUTE',
                  after: getTimestampFromDate(value),
                  before: getBeforeTimestamp(dateClause),
                })
              }
              data-testid="from-date"
              validationLabel={fromValidationLabel}
              validationStatus={fromValidationStatus}
            />
          )}
          {dateClauseOption === 'BETWEEN' && (
            <DateField
              label={
                (
                  <FormattedMessage message={_t('To date, inclusive ({format})')} format={COMMON_DATE_FORMAT} />
                ) as unknown as string
              }
              value={getDateFromTimestamp(getBeforeTimestamp(dateClause))}
              onChange={(value) =>
                onChange({
                  type: 'ABSOLUTE',
                  after: getAfterTimestamp(dateClause),
                  before: getTimestampFromDate(value),
                })
              }
              data-testid="to-date"
            />
          )}
          {dateClauseOption === 'LAST_N_DAYS' && (
            <DebouncedTextField
              css={css`
                ${theme.breakpoints.down('xs')} {
                  width: 100%;
                  max-width: none;
                }
              `}
              label={_t('Number of days')}
              defaultValue={String(getDays(dateClause) ?? '')}
              type="number"
              inputProps={{ inputMode: 'numeric' }}
              onChange={(value) => {
                onChange({
                  type: 'RELATIVE',
                  [nDaysAgoField]: Number(value),
                });
              }}
              data-testid="number-of-days"
            />
          )}
        </div>
      </AccordionPanel>
    </Accordion>
  );
};

export default FilterDateClause;
